import React, { useState, useEffect, useRef, memo } from 'react';
import LabelContentRenderer from '../../components/label-content';
import { getGdsTheme, getGdsCssVars } from '../../config/gdsThemes';
import './styles/LabelView.css';

// 将COS URL转换为代理URL（避免CORS问题）
// 注意：此函数已迁移到 utils/urlProxy.js，保留此函数以保持向后兼容
import { getProxyUrl } from '../../utils/urlProxy';
const getProxyImageUrl = (cosUrl) => {
  return getProxyUrl(cosUrl);
};

/**
 * LabelCanvas
 * 统一的标签页面骨架：标题区域 + 内容列表 + 页脚
 * 用于：实际访问页与编辑页预览，确保所见即所得
 */
function LabelCanvas({ data, phonePreview = false, embed = false, enableProgressiveRender = true }) {
  const [visibleItems, setVisibleItems] = useState(enableProgressiveRender ? 0 : Infinity);

  // 获取内容项（提前获取，避免在useEffect中使用data导致问题）
  const contentItems = data?.contentItems || [];

  // 渐进式渲染：只在首次加载时执行动画，后续data更新直接显示全部
  const progressiveInitRef = useRef(false);

  useEffect(() => {
    // 禁用渐进式渲染或嵌入模式：直接显示全部
    if (!enableProgressiveRender || embed) {
      setVisibleItems(Infinity);
      return;
    }

    // 没有数据时不处理
    if (!data) return;

    // 已经初始化过：直接显示全部内容（懒加载更新时）
    if (progressiveInitRef.current) {
      console.log('[LabelCanvas] 懒加载更新，显示全部内容');
      setVisibleItems(Infinity);
      return;
    }

    // 首次初始化：执行渐进式渲染动画
    progressiveInitRef.current = true;
    console.log('[LabelCanvas] 首次初始化渐进式渲染');

    setVisibleItems(1);

    const totalItems = contentItems.length;
    let currentIndex = 1;

    const timer = setInterval(() => {
      currentIndex += 1;
      if (currentIndex >= totalItems) {
        setVisibleItems(Infinity);
        clearInterval(timer);
      } else {
        setVisibleItems(currentIndex);
      }
    }, 80);

    return () => clearInterval(timer);
  }, [data, contentItems.length, enableProgressiveRender, embed]);

  // 监控data变化时的滚动位置
  useEffect(() => {
    if (data) {
      console.log('[LabelCanvas] data变化, 当前滚动位置:', window.scrollY);
    }
  }, [data]);

  // 早期返回必须在所有Hooks之后
  if (!data) return null;

  // 检测是否应该使用标准食品标签模板
  // 如果包含产品图、产品基础信息、营养成分表、食用方法、检验报告、相关链接，认为是标准模板
  const contentTypes = contentItems.map(item => item.contentType || item.type);
  const hasStandardTemplateComponents = 
    contentTypes.includes('image') &&
    contentTypes.includes('product-basic') &&
    contentTypes.includes('nutrition') &&
    contentTypes.includes('usage') &&
    contentTypes.includes('inspection') &&
    contentTypes.includes('related-links');
  
  // 读取全局meta优先控制模板类型
  const meta = data.meta || {};
  const variant = meta.templateVariant;
  const gdsTheme = getGdsTheme(variant);

  // templateType 决定「内容块的布局方式」，GDS 系列沿用标准食品标签的布局（表格版），仅换皮肤
  let templateType;
  let templateClassName = ''; // 根节点皮肤类名
  if (variant === 'blue-white') {
    templateType = 'blue-white-template';
    templateClassName = 'label-content-blue-white-template';
  } else if (variant === 'brown-v1') {
    templateType = 'standard-food-label';
    templateClassName = 'label-content-standard-template';
  } else if (gdsTheme) {
    templateType = 'standard-food-label';
    // 统一骨架 + 配色修饰类，4 款配色共用一套 CSS
    templateClassName = `label-content-gds-template gds-theme-${gdsTheme.suffix}`;
  } else {
    // 兼容旧数据：没有variant时按启发式
    const hasBlueWhiteTitleConfig = contentItems.some(item => {
      const itemData = item.contentData || item.data || {};
      return itemData.titleConfig?.enabled === true;
    });
    templateType = hasStandardTemplateComponents 
      ? (hasBlueWhiteTitleConfig ? 'blue-white-template' : 'standard-food-label')
      : undefined;
    if (templateType === 'standard-food-label') templateClassName = 'label-content-standard-template';
    else if (templateType === 'blue-white-template') templateClassName = 'label-content-blue-white-template';
  }

  // GDS 主题色以 CSS 变量形式下发，保证同一套样式可复用到 4 种配色
  const gdsCssVars = gdsTheme ? getGdsCssVars(variant) : {};
  
  // 可选：开发时打开调试日志

  // 背景组件优先：寻找最后一个 background 项目来定义整页背景
  const backgroundItem = [...contentItems].reverse().find(i => (i.contentType || i.type) === 'background');
  const bgData = backgroundItem ? (backgroundItem.contentData || backgroundItem.data || {}) : {};
  const hasGradient = !!bgData?.gradient?.enabled;
  const backgroundImageCss = bgData?.image 
    ? `url(${getProxyImageUrl(bgData.image)})`
    : (data.backgroundImage ? `url(${getProxyImageUrl(data.backgroundImage)})` : 'none');

  const content = (
    <div
      className={`label-view-content ${templateClassName} ${meta?.decorations?.enabled ? 'has-decorations' : ''}`}
      style={{
        // 背景优先级：背景组件 > 主题底色 > 旧的label背景（兼容）> 默认白色
        backgroundColor: bgData?.color || (gdsTheme
          ? gdsTheme.paper
          : ((templateType === 'standard-food-label' || templateType === 'blue-white-template') ? '#ffffff' : (data.backgroundColor || '#ffffff'))),
        borderColor: (templateType === 'standard-food-label' || templateType === 'blue-white-template') ? undefined : data.borderColor,
        ...gdsCssVars,
        backgroundImage: hasGradient
          ? `linear-gradient(${bgData.gradient?.angle || 180}deg, ${bgData.gradient?.from || '#ffffff'}, ${bgData.gradient?.to || '#ffffff'})${bgData?.image ? `, ${backgroundImageCss}` : ''}`
          : backgroundImageCss,
        backgroundSize: bgData?.size || 'cover',
        backgroundPosition: bgData?.position || 'center',
        backgroundRepeat: bgData?.repeat || 'no-repeat',
      }}
    >
      {data.labelName && ((meta.headerVisible !== undefined) ? meta.headerVisible : (templateType !== 'standard-food-label' || !!gdsTheme)) && (() => {
        const hs = meta?.headerStyle || {};
        const bg = (() => {
          const mode = hs.bgMode || 'gradient';
          if (mode === 'none') return 'transparent';
          if (mode === 'solid') return hs.bgColor || 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)';
          // gradient
          const from = hs.gradientFrom || '#2563EB';
          const to = hs.gradientTo || '#1D4ED8';
          return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
        })();
        const align = hs.align || 'center';
        const paddingY = (typeof hs.paddingY === 'number' ? hs.paddingY : 20);
        const paddingX = (typeof hs.paddingX === 'number' ? hs.paddingX : 20);
        const titleStyle = {
          color: hs.textColor || '#ffffff',
          fontSize: (typeof hs.fontSize === 'number' ? hs.fontSize : 24) + 'px',
          fontWeight: hs.fontWeight || 700,
          margin: 0,
          textShadow: hs.textShadow === false ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.2)'
        };
        return (
          <div className="label-header" style={{ background: bg, padding: `${paddingY}px ${paddingX}px`, textAlign: align }}>
            <h1 className="label-title" style={titleStyle}>{data.labelName}</h1>
          </div>
        );
      })()}

      {/* 背景遮罩 */}
      {bgData?.overlayOpacity > 0 ? (
        <div style={{ position: 'absolute', inset: 0, background: bgData.overlayColor || '#000', opacity: bgData.overlayOpacity, pointerEvents: 'none', borderRadius: 'inherit' }} />
      ) : null}

      {/* 水印层 */}
      {meta?.watermark?.enabled !== false && (
        <div className="label-watermark">
          <img 
            src={`${process.env.PUBLIC_URL || ''}/images/huapu-logo.jpg`} 
            alt="数字标签内测水印" 
            className="watermark-image"
          />
        </div>
      )}

      <div className="label-items" style={{ gap: (meta?.layout?.blockGap != null ? meta.layout.blockGap : 12) + 'px' }}>
        {(contentItems).map((item, index) => {
          // 渐进式渲染：只渲染当前可见的项
          const isVisible = index < visibleItems;

          // 注意：contentData为null表示懒加载中，需要保持null传递给渲染器显示骨架屏
          const rawData = item.contentData !== undefined ? item.contentData : item.data;
          const itemData = rawData === null ? null : (rawData || {});
          const cs = itemData?.containerStyle;
          let style = undefined;
          if (cs?.enabled) {
            const boxShadow = cs.shadow === 'none' ? 'none' : (cs.shadow === 'medium' ? '0 8px 24px rgba(0,0,0,0.10)' : '0 4px 12px rgba(0,0,0,0.06)');
            style = {
              background: cs.bgColor || undefined,
              padding: (typeof cs.padding === 'number' ? cs.padding : 16) + 'px',
              borderRadius: (typeof cs.radius === 'number' ? cs.radius : 12) + 'px',
              border: (cs.borderWidth || 0) > 0 ? `${cs.borderWidth}px solid ${cs.borderColor || '#E5E7EB'}` : 'none',
              boxShadow,
            };
          }

          // 添加淡入动画类名
          const animClass = cs?.animation && cs.animation !== 'none' ? `anim-${cs.animation}` : '';
          const progressiveAnimClass = enableProgressiveRender && !embed ? 'label-item-progressive' : '';

          const itemStyle = { ...style };
          if (cs?.animation && cs.animation !== 'none') {
            itemStyle.animationDuration = (cs.animationDuration || 400) + 'ms';
          }

          // 未到显示时间的项不渲染
          if (!isVisible) {
            return null;
          }

          // 使用稳定的key：优先使用itemId
          const stableKey = item.itemId || `item-${index}`;

          return (
            <div key={stableKey} className={`label-item ${animClass} ${progressiveAnimClass}`} style={itemStyle}>
              <LabelContentRenderer
                item={{ type: item.contentType || item.type, data: itemData }}
                mode="view"
                templateType={templateType}
                priority={index === 0} // 第一个内容项高优先级加载
              />
            </div>
          );
        })}
      </div>

      {meta?.footerHidden ? null : (meta?.footerText !== undefined && meta?.footerText !== null && meta?.footerText !== '') ? (
        <div className={`label-footer ${templateType === 'standard-food-label' ? 'standard-footer' : ''} ${gdsTheme ? 'gds-footer' : ''}`}>
          <p className="footer-brand">{meta.footerText}</p>
        </div>
      ) : (
        templateType === 'standard-food-label' ? (
          <div className={`label-footer standard-footer ${gdsTheme ? 'gds-footer' : ''}`}>
            <p className="footer-brand">技术支持： 数字标签内测</p>
          </div>
        ) : (
          <div className="label-footer">
            <p className="footer-brand">数字标签内测 · 数字标签系统</p>
            <p className="footer-time">访问时间：{new Date().toLocaleString('zh-CN')}</p>
          </div>
        )
      )}
    </div>
  );

  if (embed) {
    // 编辑页右侧预览：仅返回内容容器，不包裹外层背景容器
    return content;
  }

  return (
    <div className={`label-view-container ${phonePreview ? 'phone-preview' : ''}`}>
      {content}
    </div>
  );
}

// 使用memo包装，只有data实际变化时才重新渲染
export default memo(LabelCanvas);
